if (!customElements.get('search-filter')) {
	class SearchFilter extends HTMLElement {
		constructor() {
			super();
		}

		connectedCallback() {
			this.init();
		}

		async init() {
			this.elements = {
				filterDetails: this.querySelectorAll('details'),
				filterSummary: this.querySelectorAll('summary'),
				loadingSpinner: this.querySelector('.loading__spinner'),
			};

			await this.initialFilterRendering();

			this.elements.filterSubmitButton = this.querySelector('.filter-value');
			if (!this.elements.filterSubmitButton) return;

			if (!this.elements.filterSubmitButton.hasAttribute('data-previous-href')) {
				this.elements.filterSubmitButton.setAttribute('data-previous-href', this.elements.filterSubmitButton.getAttribute('href'));
			}

			this.previousHref = this.elements.filterSubmitButton.getAttribute('data-previous-href');

			this.elements.filterDetails.forEach(item => {
				item.addEventListener('click', this.closeDetails.bind(this, item));
				const summary = item.querySelector('summary');
				if (summary) {
					summary.setAttribute('tabindex', '0');
					summary.addEventListener('keydown', this.handleKeyDown.bind(this, item));
					item.addEventListener('focusout', this.handleFocusOut.bind(this, item));
				}
			});

			document.addEventListener('click', this.closeAllDetails.bind(this));
			this.bindFilterItems();
		}

		bindFilterItems() {
			this.elements.filterItems = this.querySelectorAll('.filter-select__item:not(.holder)');
			this.elements.filterItems.forEach(item => {
				if (item.dataset.searchFilterBound === 'true') return;
				item.dataset.searchFilterBound = 'true';
				item.addEventListener('click', this.onClick.bind(this, item));
				item.setAttribute('tabindex', '0');
				item.addEventListener('keydown', this.handleItemKeyDown.bind(this, item));
			});
		}

		handleKeyDown(details, event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				details.open = !details.open;
			}
		}

		handleItemKeyDown(item, event) {
			if (event.key === 'Enter' || event.key === ' ') {
				event.preventDefault();
				this.onClick(item);
			}
		}

		closeDetails(clickedItem) {
			this.elements.filterDetails.forEach(item => {
				if (item !== clickedItem) {
					item.removeAttribute('open');
				}
			});
		}

		closeAllDetails(event) {
			if (!Array.from(this.elements.filterDetails).some(item => item.contains(event.target))) {
				this.elements.filterDetails.forEach(item => {
					item.removeAttribute('open');
				});
			}
		}

		handleFocusOut(details) {
			setTimeout(() => {
				if (!details.contains(document.activeElement)) {
					details.removeAttribute('open');
				}
			}, 0);
		}

		onClick(clickedItem) {
			const parentDetails = clickedItem.closest('details');
			const summaryElement = parentDetails.querySelector('summary span');
			const clickedIndex = Array.from(this.elements.filterDetails).indexOf(parentDetails);

			if (summaryElement) {
				summaryElement.textContent = clickedItem.textContent;
			}

			clickedItem.classList.toggle('active');
			const otherItems = parentDetails.querySelectorAll('.filter-select__item:not(.holder)');
			otherItems.forEach(item => {
				if (item !== clickedItem) item.classList.remove('active');
			});

			parentDetails.removeAttribute('open');

			// Changing Manufacturer invalidates Model + Sub-model.
			// Changing Model invalidates Sub-model.
			if (clickedIndex === 0) {
				this.resetDependentFilter(1, 'Model');
				this.resetDependentFilter(2, 'Sub-model');
			} else if (clickedIndex === 1) {
				this.resetDependentFilter(2, 'Sub-model');
			}

			this.updateFilters(clickedIndex);
		}

		resetDependentFilter(index, label) {
			const detail = this.elements.filterDetails[index];
			if (!detail) return;
			const summary = detail.querySelector('summary span');
			const list = detail.querySelector('.filter-select');
			if (summary) summary.textContent = label;
			if (list) {
				list.innerHTML = `<li class="filter-select__item holder">${label}...</li>`;
			}
		}

		getFacetValues(parsedHTML, label) {
			const results = new Map();

			let paramName = '';
			if (label === 'Model') paramName = 'filter.p.m.custom.model';
			if (label === 'Sub-model') paramName = 'filter.p.m.custom.sub_model';

			if (paramName) {
				parsedHTML.querySelectorAll(`input[name="${paramName}"][data-filter-value-name][data-filter]`).forEach(input => {
					const name = input.getAttribute('data-filter-value-name');
					const filterId = input.getAttribute('data-filter');
					if (name && filterId && !input.disabled && !results.has(name)) {
						results.set(name, filterId);
					}
				});
			}

			if (results.size === 0) {
				const facets = parsedHTML.querySelectorAll('.facets__summary');
				facets.forEach(facet => {
					const labelElement = facet.querySelector('.facets__summary-label');
					if (!labelElement || labelElement.textContent.trim() !== label) return;
					const container = facet.closest('details') || facet.parentElement;
					if (!container) return;
					container.querySelectorAll('[data-filter-value-name][data-filter]').forEach(input => {
						const name = input.getAttribute('data-filter-value-name');
						const filterId = input.getAttribute('data-filter');
						if (name && filterId && !input.disabled && !results.has(name)) results.set(name, filterId);
					});
				});
			}

			return results;
		}

		async getProductMetafieldValues(requestUrl, label) {
			const sectionId = label === 'Model' ? 'model-filter-values' : 'submodel-filter-values';
			const paramName = label === 'Model' ? 'filter.p.m.custom.model' : 'filter.p.m.custom.sub_model';
			const baseUrl = new URL(requestUrl, window.location.origin);
			baseUrl.searchParams.set('section_id', sectionId);

			const values = new Map();
			let page = 1;
			let totalPages = 1;

			while (page <= totalPages) {
				const pageUrl = new URL(baseUrl.href);
				if (page > 1) pageUrl.searchParams.set('page', page);

				const response = await fetch(pageUrl.href, { credentials: 'same-origin' });
				if (!response.ok) throw new Error(`HTTP ${response.status}`);
				const html = await response.text();
				const parsed = new DOMParser().parseFromString(html, 'text/html');
				const container = parsed.querySelector('.meka-filter-values');
				if (!container) throw new Error(`Missing ${sectionId} section`);

				totalPages = Math.max(totalPages, parseInt(container.dataset.pages || '1', 10));
				container.querySelectorAll('[data-filter-value]').forEach(node => {
					const value = node.getAttribute('data-filter-value') || '';
					if (value && !values.has(value)) {
						values.set(value, `${paramName}=${encodeURIComponent(value)}`);
					}
				});

				page += 1;
			}

			return values;
		}
		async rebuildFilter(index, parsedHTML, label, requestUrl) {
			const detail = this.elements.filterDetails[index];
			if (!detail) return;
			const list = detail.querySelector('.filter-select');
			const summary = detail.querySelector('summary span');
			if (!list) return;

			let values;
			try {
				// Primary source: actual product metafields from the filtered collection.
				// This bypasses Shopify's 100-value storefront facet limit completely.
				values = await this.getProductMetafieldValues(requestUrl, label);
			} catch (error) {
				console.warn(`Could not load ${label} values from product metafields; using Shopify facets.`, error);
				values = this.getFacetValues(parsedHTML, label);
			}

			list.innerHTML = '';
			if (values.size === 0) {
				list.innerHTML = `<li class="filter-select__item holder">${label}...</li>`;
				if (summary) summary.textContent = label;
				return;
			}

			values.forEach((filterId, filterValueName) => {
				list.append(this.createFilterHtml(filterId, filterValueName));
			});
			if (summary) summary.textContent = label;
			this.bindFilterItems();
		}

		updateFilters(clickedIndex = -1) {
			const activeItems = this.querySelectorAll('.filter-select__item.active:not(.holder)');
			const dataFilterValues = Array.from(activeItems).map(item => item.getAttribute('data-filter')).join('&');
			const currentValue = (this.previousHref || '').replace(/[?&]+$/, '');
			const requestUrl = dataFilterValues ? `${currentValue}?${dataFilterValues}` : currentValue;

			this.elements.filterSubmitButton.setAttribute('href', requestUrl);
			this.elements.filterSubmitButton.removeAttribute('aria-disabled');

			fetch(requestUrl, { credentials: 'same-origin' })
				.then(response => {
					if (!response.ok) throw new Error(`HTTP ${response.status}`);
					return response.text();
				})
				.then(async responseText => {
					const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');

					// Keep the original disabled-state behavior for the currently rendered options.
					const disabledElements = parsedHTML.querySelectorAll('facet-filters-form .list-menu__item input[disabled]');
					this.elements.filterItems.forEach(filterItem => {
						const filterItemValue = filterItem.getAttribute('data-filter');
						filterItem.classList.remove('disabled');
						filterItem.removeAttribute('disabled');
						filterItem.setAttribute('tabindex', '0');

						disabledElements.forEach(disabledElement => {
							if (filterItemValue === disabledElement.getAttribute('data-filter')) {
								filterItem.classList.add('disabled');
								filterItem.setAttribute('disabled', '');
								filterItem.setAttribute('tabindex', '-1');
							}
						});
					});

					// IMPORTANT: rebuild dependent dropdowns from the filtered collection response.
					// This avoids Shopify's global 100-value storefront filter list.
					if (clickedIndex === 0) {
						await Promise.all([
							this.rebuildFilter(1, parsedHTML, 'Model', requestUrl),
							this.rebuildFilter(2, parsedHTML, 'Sub-model', requestUrl)
						]);
					} else if (clickedIndex === 1) {
						await this.rebuildFilter(2, parsedHTML, 'Sub-model', requestUrl);
					}

					this.bindFilterItems();
				})
				.catch(error => console.error('Search filter update failed:', error));
		}

		async initialFilterRendering() {
			this.elements.loadingSpinner.classList.remove('hidden');
			this.setAttribute('data-loading', 'true');
			if (this.dataset.collection != 'all') {
				await this.loadFilters(this.dataset.filterUrl);
			}
			this.removeAttribute('data-loading');
			this.elements.loadingSpinner.classList.add('hidden');
		}

		async loadFilters(url) {
			if (!this.elements.filterDetails) return;

			const res = await fetch(url);
			const html = await res.text();
			const parsedHTML = new DOMParser().parseFromString(html, 'text/html');

			const filtersForm = parsedHTML.querySelector('facet-filters-form');
			let summarySelector = '.facets__summary-label';
			if (!filtersForm) return;

			let filters = filtersForm.querySelectorAll('.facets__summary');
			if (filters.length == 0) {
				filters = filtersForm.querySelectorAll('.mobile-facets__summary');
				summarySelector = 'div > span';
			}
			if (filters.length == 0) return;

			this.elements.filterDetails.forEach(detail => {
				const summary = detail.querySelector('summary > span');
				const filterSelect = detail.querySelector('.filter-select');
				if (!summary || !filterSelect) return;
				const summaryText = summary.innerText.trim();

				filters.forEach(filter => {
					const filterLabel = filter.querySelector(`${summarySelector}`);
					if (!filterLabel || !filterLabel.innerText.includes(summaryText)) return;

					const filterValues = filter.parentElement.querySelectorAll('[data-filter-value-name]');
					const filterValuesMap = new Map();
					filterValues.forEach(value => {
						const { filterValueName } = value.dataset;
						const filterId = value.dataset.filter;
						if (!filterValuesMap.has(filterValueName)) {
							filterValuesMap.set(filterValueName, { filterValueName, filterId });
						}
					});

					filterValuesMap.forEach(({ filterValueName, filterId }) => {
						filterSelect.append(this.createFilterHtml(filterId, filterValueName));
					});
				});
			});
		}

		createFilterHtml(filterId, label) {
			const li = document.createElement('li');
			li.className = 'filter-select__item';
			li.setAttribute('tabindex', '0');
			li.setAttribute('role', 'button');
			li.setAttribute('data-filter', filterId);
			li.innerText = label;
			return li;
		}
	}

	customElements.define('search-filter', SearchFilter);
}
